import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Loader2,
  User,
  KeyRound,
  Hash,
  Eye,
  EyeOff,
  ArrowRight,
  Wallet,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useCustomerKiosk, useKioskOrganization, useKioskLocation } from '@/hooks/useCustomerKiosk';

const usernameSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});

const pinSchema = z.object({
  customerNumber: z.string().min(1, 'Customer number is required'),
  pin: z.string().regex(/^\d{4,6}$/, 'PIN must be 4-6 digits'),
});

type UsernameFormValues = z.infer<typeof usernameSchema>;
type PinFormValues = z.infer<typeof pinSchema>;

export default function CustomerKioskLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get organization and location from URL params (can be UUID or slug/code)
  const orgParam = searchParams.get('org');
  const locParam = searchParams.get('loc');

  // Fetch organization first (by UUID or slug)
  const { data: organization, isLoading: orgLoading, error: orgError } = useKioskOrganization(orgParam);
  
  // Use the resolved organization ID for other hooks
  const organizationId = organization?.id || null;

  const {
    session,
    isAuthenticated,
    isInitialized,
    loginWithPassword,
    loginWithPin,
  } = useCustomerKiosk(organizationId);

  // Fetch location (by UUID or code, using resolved org ID)
  const { data: location } = useKioskLocation(locParam, organizationId);

  const [showPassword, setShowPassword] = useState(false);
  const [authMode, setAuthMode] = useState<'username' | 'pin'>('username');

  // Username/Password form
  const usernameForm = useForm<UsernameFormValues>({
    resolver: zodResolver(usernameSchema),
    defaultValues: { username: '', password: '' },
  });

  // PIN form
  const pinForm = useForm<PinFormValues>({
    resolver: zodResolver(pinSchema),
    defaultValues: { customerNumber: '', pin: '' },
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isInitialized && isAuthenticated && session) {
      const orderUrl = locParam
        ? `/customer-kiosk/order?org=${orgParam}&loc=${locParam}`
        : `/customer-kiosk/order?org=${orgParam}`;
      navigate(orderUrl, { replace: true });
    }
  }, [isInitialized, isAuthenticated, session, navigate, orgParam, locParam]);

  const handleUsernameLogin = async (data: UsernameFormValues) => {
    try {
      await loginWithPassword.mutateAsync(data);
      
      const orderUrl = locParam
        ? `/customer-kiosk/order?org=${orgParam}&loc=${locParam}`
        : `/customer-kiosk/order?org=${orgParam}`;
      navigate(orderUrl);
    } catch (error: any) {
      toast.error('Login Failed', {
        description: error?.message || 'Invalid username or password',
      });
    }
  };

  const handlePinLogin = async (data: PinFormValues) => {
    try {
      await loginWithPin.mutateAsync({
        customerNumber: data.customerNumber,
        pin: data.pin,
      });

      const orderUrl = locParam
        ? `/customer-kiosk/order?org=${orgParam}&loc=${locParam}`
        : `/customer-kiosk/order?org=${orgParam}`;
      navigate(orderUrl);
    } catch (error: any) {
      toast.error('Login Failed', {
        description: error?.message || 'Invalid customer number or PIN',
      });
    }
  };

  // Show error if no organization configured
  if (!orgParam) {
    const isDev = import.meta.env.DEV;
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-destructive">Configuration Error</CardTitle>
            <CardDescription>
              This kiosk is not properly configured. Please contact staff for assistance.
            </CardDescription>
          </CardHeader>
          {isDev && (
            <CardContent className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                <strong>Dev Mode:</strong> Add URL parameters to configure the kiosk:
              </p>
              <code className="block bg-muted p-2 rounded text-xs break-all">
                /customer-kiosk?org=ORG_SLUG&loc=LOC_CODE
              </code>
              <p className="text-xs text-muted-foreground">
                Find org slug and location code in Command Center → Locations.
                Example: <code className="bg-muted px-1">?org=demo-restaurant&loc=MAIN-0001</code>
              </p>
            </CardContent>
          )}
        </Card>
      </div>
    );
  }

  // Loading state
  if (!isInitialized || orgLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 text-primary animate-spin" />
          <p className="text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  // Organization not found error
  if (orgError || !organization) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-destructive">Organization Not Found</CardTitle>
            <CardDescription>
              The organization "{orgParam}" could not be found. Please check the URL and try again.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground">
              Contact staff for the correct kiosk URL.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/20 via-transparent to-transparent" />

      <div className="relative w-full max-w-md">
        {/* Logo/Branding */}
        <div className="text-center mb-8">
          {organization?.logo_url ? (
            <img
              src={organization.logo_url}
              alt={organization.name}
              className="h-16 mx-auto mb-4"
            />
          ) : (
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/20 flex items-center justify-center">
              <Wallet className="h-8 w-8 text-primary" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-white">{organization?.name || 'Self-Service'}</h1>
          {location && (
            <p className="text-white/60 text-sm mt-1">{location.name}</p>
          )}
          <p className="text-primary font-medium text-sm mt-2 uppercase tracking-wider">Self Serve</p>
        </div>

        <Card className="border-0 shadow-2xl">
          <CardHeader className="text-center pb-2">
            <CardTitle>Welcome Back</CardTitle>
            <CardDescription>Sign in to your account to place an order</CardDescription>
          </CardHeader>

          <CardContent>
            <Tabs value={authMode} onValueChange={(v) => setAuthMode(v as 'username' | 'pin')}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="username" className="gap-2">
                  <User className="h-4 w-4" />
                  Username
                </TabsTrigger>
                <TabsTrigger value="pin" className="gap-2">
                  <Hash className="h-4 w-4" />
                  Quick PIN
                </TabsTrigger>
              </TabsList>

              {/* Username/Password Login */}
              <TabsContent value="username">
                <form onSubmit={usernameForm.handleSubmit(handleUsernameLogin)} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="username">Username</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="username"
                        {...usernameForm.register('username')}
                        placeholder="Enter your username"
                        className="pl-10"
                        autoComplete="username"
                      />
                    </div>
                    {usernameForm.formState.errors.username && (
                      <p className="text-xs text-destructive">
                        {usernameForm.formState.errors.username.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        {...usernameForm.register('password')}
                        placeholder="Enter your password"
                        className="pl-10 pr-10"
                        autoComplete="current-password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    {usernameForm.formState.errors.password && (
                      <p className="text-xs text-destructive">
                        {usernameForm.formState.errors.password.message}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    variant="blaze"
                    className="w-full gap-2"
                    disabled={loginWithPassword.isPending}
                  >
                    {loginWithPassword.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        Sign In
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>

              {/* PIN Login */}
              <TabsContent value="pin">
                <form onSubmit={pinForm.handleSubmit(handlePinLogin)} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="customerNumber">Customer Number</Label>
                    <div className="relative">
                      <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="customerNumber"
                        {...pinForm.register('customerNumber')}
                        placeholder="6-digit customer number"
                        className="pl-10 font-mono text-lg tracking-wider"
                        maxLength={6}
                      />
                    </div>
                    {pinForm.formState.errors.customerNumber && (
                      <p className="text-xs text-destructive">
                        {pinForm.formState.errors.customerNumber.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pin">PIN</Label>
                    <div className="relative">
                      <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="pin"
                        type="password"
                        {...pinForm.register('pin')}
                        placeholder="4-6 digit PIN"
                        className="pl-10 font-mono text-lg tracking-wider"
                        maxLength={6}
                        inputMode="numeric"
                        pattern="[0-9]*"
                      />
                    </div>
                    {pinForm.formState.errors.pin && (
                      <p className="text-xs text-destructive">
                        {pinForm.formState.errors.pin.message}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    variant="blaze"
                    className="w-full gap-2"
                    disabled={loginWithPin.isPending}
                  >
                    {loginWithPin.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        Sign In
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>

            <p className="text-xs text-center text-muted-foreground mt-6">
              Don&apos;t have an account? Please ask a staff member to create one for you.
            </p>
          </CardContent>
        </Card>

        {/* Help Text */}
        <p className="text-center text-white/40 text-xs mt-6">
          Need help? Ask a staff member for assistance.
        </p>
      </div>
    </div>
  );
}

